import _ from 'lodash';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

import { apiAxios } from '@/apiAxios';
import Product from '@/classes/Product';
import { IApiResponseProductsList, IApiResponseProductsSearch, IApiResponseProductsUpdate } from '@/interfaces';
import { upsert } from '@/main';
import store from '@/store/store';

@Module({ dynamic: true, store, name: 'ProductsModule', namespaced: true })
export default class ProductsModule extends VuexModule {
  public products = [] as Product[];
  public currentPage = 0;
  public hasMore = true;

  @Mutation
  public setHasMore(newHasMore: boolean) {
    this.hasMore = newHasMore;
  }

  @Mutation
  public increasePageIndex() {
    this.currentPage++;
  }

  @Mutation
  public upsertProducts(newProducts: Product[]) {
    const results = this.products;
    _.each(newProducts, (newProduct) => {
      upsert<Product>(results, { productid: newProduct.productid }, newProduct);
    });
    this.products = results;
  }

  @Mutation
  public upsertProduct(newProduct: Product) {
    const results = this.products;
    upsert<Product>(results, { productid: newProduct.productid }, newProduct);

    this.products = results;
  }

  @Mutation
  public prependProduct(newProduct: Product) {
    const results = this.products;
    results.unshift(newProduct);

    this.products = results;
  }

  @Action
  public async apiLoadNextPage(arg: { pageSize: number }): Promise<IApiResponseProductsList> {
    try {
      const { pageSize } = arg;

      const response = await apiAxios.get<IApiResponseProductsList>(`api/product/page/${ this.currentPage }`, {
        params: { pagesize: pageSize },
      });
      const products = response.data.payload.products;

      if (products.length > 0) {
        this.upsertProducts(response.data.payload.products);
        this.increasePageIndex();

        if (products.length < pageSize) {
          this.setHasMore(false);
        }
      } else {
        // No item
        this.setHasMore(false);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiAddProduct(arg: { product: Product }): Promise <IApiResponseProductsUpdate> {
    try {
      const { product } = arg;

      const response = await apiAxios.post<IApiResponseProductsUpdate>('api/product/create', product);

      if (response.status === 200) {
        this.prependProduct(response.data.payload.product);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiUpdateProduct(arg: { product: Product }): Promise <IApiResponseProductsUpdate> {
    try {
      const { product } = arg;

      const response = await apiAxios.post<IApiResponseProductsUpdate>('api/product/update', product);

      this.upsertProduct(response.data.payload.product);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiSearchProducts(arg: { searchText: string }): Promise<IApiResponseProductsSearch> {
    try {
      const { searchText } = arg;

      const response = await apiAxios.post<IApiResponseProductsSearch>('api/product/search', { searchText });

      this.upsertProducts(response.data.payload.products);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}
